import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { differenceInMinutes, differenceInSeconds } from 'date-fns';
import isPastMeeting from '@medflex/utils/isPastMeeting';
import { STATUS, TYPE } from '../../store/models/sessionMeetings';
import useUserProfile from '../../hooks/useUserProfile';

const getDurationString = (startDate, endDate) => {
  const minutes = Math.max(0, differenceInMinutes(new Date(endDate), new Date(startDate)));
  const seconds = Math.max(0, differenceInSeconds(new Date(endDate), new Date(startDate)) % 60);
  return `${minutes}:${seconds > 9 ? seconds : `0${seconds}`} Min`;
};

const getAttendeeFromParticipantsByUserId = userId => participants => {
  if (participants.length > 2) return null;
  const { avatar, firstName, lastName } = participants.find(p => p.userId !== userId);

  return {
    avatar,
    initials: `${firstName[0]}${lastName[0]}`,
  };
};

const useSessionMeetingsList = () => {
  const dispatch = useDispatch();
  const { meetings = [], status } = useSelector(state => state[TYPE]);
  const { userProfile } = useUserProfile();
  const getAttendeeFromParticipants = useCallback(
    getAttendeeFromParticipantsByUserId(userProfile.id),
    [userProfile],
  );

  const filter = dispatch[TYPE].load;

  const pastMeetings = useMemo(
    () =>
      meetings.filter(isPastMeeting).map(meeting => ({
        ...meeting,
        attendee: getAttendeeFromParticipants(meeting.participants),
        duration: getDurationString(
          meeting.establishedAt || meeting.scheduledAt,
          meeting.lastTouchedAt || meeting.scheduledAt,
        ),
      })),
    [meetings],
  );

  const futureMeetings = useMemo(
    () =>
      meetings
        .filter(
          meeting =>
            !meeting.scheduleCanceledAt && !pastMeetings.map(({ id }) => id).includes(meeting.id),
        )
        .map(meeting => ({
          ...meeting,
          attendee: getAttendeeFromParticipants(meeting.participants),
        })),
    [meetings, pastMeetings],
  );

  return {
    meetings,
    pastMeetings,
    futureMeetings,
    filter,
    loading: status === STATUS.LOADING,
    success: status === STATUS.SUCCESS,
    idle: status === STATUS.IDLE,
    error: status === STATUS.ERROR,
  };
};

export default useSessionMeetingsList;
